/**
 * PageviewTracker — Phase 119 + Phase 133.42 (Welle A/B/C)
 * ---------------------------------------------------------
 * Sendet bei jedem Routenwechsel einen anonymen Pageview an das Backend.
 * Zusätzlich:
 *   - utm_content / utm_term / Seitentitel
 *   - grobe Scroll-Tiefe (25/50/75/100 %)
 *   - automatisches 404-Event, sobald der Router keine Route matcht (opt-in via helper)
 * Keine Cookies mit Personenbezug — nur ein zufälliger session_id pro Tab
 * (via sessionStorage). Wird NICHT innerhalb von /admin/* aktiv, damit der
 * eigene Traffic nicht die Statistik verfälscht.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const genSid = () => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch (_e) { /* ignore */ }
  return `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const getSessionId = () => {
  try {
    let sid = sessionStorage.getItem('ke_sid');
    if (!sid) {
      sid = genSid();
      sessionStorage.setItem('ke_sid', sid);
    }
    return sid;
  } catch (_e) {
    return genSid();
  }
};

const shouldTrack = (path) => {
  if (!path) return false;
  const p = path.toLowerCase();
  if (p.startsWith('/admin')) return false;
  if (p.startsWith('/fahrer')) return false;
  return true;
};

const readUtm = (search) => {
  try {
    const q = new URLSearchParams(search || '');
    return {
      utm_source:   q.get('utm_source') || null,
      utm_medium:   q.get('utm_medium') || null,
      utm_campaign: q.get('utm_campaign') || null,
      utm_content:  q.get('utm_content') || null,
      utm_term:     q.get('utm_term') || null,
    };
  } catch (_e) {
    return { utm_source: null, utm_medium: null, utm_campaign: null, utm_content: null, utm_term: null };
  }
};

// Scroll-Depth: ein Event pro Stufe pro Pfad pro Session
const _scrollFired = {};
const trackScrollDepth = (path) => {
  const scrollTop = window.scrollY || document.documentElement.scrollTop || 0;
  const docHeight = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight) - window.innerHeight;
  if (docHeight <= 0) return;
  const pct = Math.min(100, Math.floor((scrollTop / docHeight) * 100));
  const stages = [
    [25, 'scroll_25'],
    [50, 'scroll_50'],
    [75, 'scroll_75'],
    [100, 'scroll_100'],
  ];
  const key = path || '/';
  for (const [threshold, evName] of stages) {
    if (pct >= threshold) {
      const marker = `${key}::${evName}`;
      if (!_scrollFired[marker]) {
        _scrollFired[marker] = true;
        // eslint-disable-next-line no-use-before-define
        trackEvent(evName, { threshold });
      }
    }
  }
};

const PageviewTracker = () => {
  const location = useLocation();
  useEffect(() => {
    const path = location.pathname || '/';
    if (!shouldTrack(path)) return;
    const utm = readUtm(location.search);
    const payload = {
      session_id: getSessionId(),
      path,
      referrer: document.referrer || null,
      screen: `${window.innerWidth}x${window.innerHeight}`,
      title: (typeof document !== 'undefined' && document.title) ? document.title.slice(0, 180) : null,
      ...utm,
    };
    axios.post(`${API}/api/track/pageview`, payload).catch(() => {});

    // Scroll-Stages neu einrichten pro Pfad
    Object.keys(_scrollFired).forEach((k) => { if (k.startsWith(`${path}::`)) delete _scrollFired[k]; });
    const onScroll = () => trackScrollDepth(path);
    window.addEventListener('scroll', onScroll, { passive: true });
    // initial check (falls Seite kurz genug)
    trackScrollDepth(path);
    return () => window.removeEventListener('scroll', onScroll);
  }, [location.pathname, location.search]);
  return null;
};

/**
 * Anonymes Event-Tracking. Nutzung z.B.:
 *   trackEvent('cta_whatsapp_click', { product_id: 'abc' })
 * Whitelist im Backend — unbekannte Events werden verworfen.
 */
export const trackEvent = (eventType, meta = null) => {
  try {
    const path = (typeof window !== 'undefined' ? window.location.pathname : '/') || '/';
    if (!shouldTrack(path)) return;
    const payload = {
      session_id: getSessionId(),
      path,
      event_type: eventType,
      meta: meta && typeof meta === 'object' ? meta : null,
    };
    axios.post(`${API}/api/track/event`, payload).catch(() => {});
  } catch (_e) { /* ignore */ }
};

// Explicit helper for NotFound components
export const track404 = () => {
  try {
    trackEvent('404_view', {
      referrer: document.referrer || null,
      path: window.location.pathname,
    });
  } catch (_e) { /* ignore */ }
};

export default PageviewTracker;
